import { Component, HostListener, OnInit, computed, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { RouterOutlet } from '@angular/router';
import {
  CEI_APP_TEMPLATE_FOOTER_PLACEMENT_CONTENT,
  CeiAppTemplateComponent,
  CeiFloatingDockSidenavComponent,
  CeiPersonalDataMenuComponent,
  CeiSobreVersoesService,
  CeiToolbarUserAreaComponent,
  type CeiPersonalDataMenuItem,
} from '@cei-ufg/ngx-arquitetura-cei-frontend';
import { AutenticacaoService, SessionContextService } from '@cei-ufg/ngx-cei-sso-client';
import { catchError, EMPTY, tap } from 'rxjs';
import {
  CEI_APPS_MENU_ALTERAR_VINCULO,
  CEI_APPS_MENU_SAIR,
  CEI_APPS_MENU_SOBRE,
  PersonalDataMenuService,
} from '../../services/personal-data-menu.service';
import { PessoaApiService } from '../../components/pessoas/pessoa-api.service';
import {
  normalizePessoaFotoMeUrl,
  PESSOA_FOTO_ME_API_PATH,
} from '../../components/pessoas/pessoa-foto.paths';

/**
 * Shell autenticado do portal CEI Apps: toolbar, sidenav e outlet das rotas filhas.
 */
@Component({
  selector: 'app-portal-layout',
  standalone: true,
  imports: [
    RouterOutlet,
    CeiAppTemplateComponent,
    CeiFloatingDockSidenavComponent,
    CeiPersonalDataMenuComponent,
    CeiToolbarUserAreaComponent,
  ],
  templateUrl: './portal-layout.component.html',
  styleUrl: './portal-layout.component.scss',
})
export class PortalLayoutComponent implements OnInit {
  protected readonly CEI_APP_TEMPLATE_FOOTER_PLACEMENT_CONTENT =
    CEI_APP_TEMPLATE_FOOTER_PLACEMENT_CONTENT;

  private readonly autenticacaoService = inject(AutenticacaoService);
  private readonly personalDataMenuService = inject(PersonalDataMenuService);
  private readonly sobreVersoesService = inject(CeiSobreVersoesService);
  private readonly pessoaApi = inject(PessoaApiService);
  protected readonly sessionContext = inject(SessionContextService);

  protected readonly menuAberto = signal(false);

  protected readonly personalMenuItems = computed(() =>
    this.personalDataMenuService.buildMenuItems(this.pessoaId()),
  );

  protected readonly userId = this.sessionContext.userId;
  protected readonly userLogin = this.sessionContext.login;
  protected readonly pessoaId = this.sessionContext.pessoaId;

  /**
   * Toolbar: foto do login autenticado no `apiUrl` — path estável sem PK de ambiente.
   */
  protected readonly fotoToolbarUrl = computed(() => {
    const fotoUrl = this.sessionContext.details()?.foto_url?.trim();
    if (!fotoUrl) {
      return undefined;
    }
    return PESSOA_FOTO_ME_API_PATH;
  });

  ngOnInit(): void {
    // Descarta claims de outro ambiente; em seguida resolve pessoa/foto no domínio por login.
    this.sessionContext.pessoaId.set(undefined);
    this.sessionContext.patchDetails({ pessoa_id: null, foto_url: null });
    this.sincronizarPessoaDominioPorLogin();
  }

  /**
   * Após login: GET autenticado por login no domínio configurado.
   * Preenche sessão/toolbar sem abrir a tela de dados pessoais.
   */
  private sincronizarPessoaDominioPorLogin(): void {
    this.pessoaApi
      .retrieveMe()
      .pipe(
        tap((model) => {
          if (model.id) {
            this.sessionContext.pessoaId.set(model.id);
          }
          this.sessionContext.patchDetails({
            pessoa_id: model.id ?? null,
            foto_url: normalizePessoaFotoMeUrl(model.foto_url),
            nome: model.nome,
          });
        }),
        catchError((error: unknown) => {
          // 404 = ainda sem pessoa no domínio para este login — esperado até o Cadastro.
          if (!(error instanceof HttpErrorResponse && error.status === 404)) {
            console.warn('Falha ao sincronizar pessoa/foto do domínio por login.', error);
          }
          return EMPTY;
        }),
      )
      .subscribe();
  }

  protected togglePersonalMenu(event: Event): void {
    event.stopPropagation();
    this.menuAberto.update((open) => !open);
  }

  protected onPersonalMenuItemClick(item: CeiPersonalDataMenuItem): void {
    const action = this.personalDataMenuService.resolveItemAction(item);

    if (action === CEI_APPS_MENU_ALTERAR_VINCULO) {
      this.menuAberto.set(false);
      return;
    }

    if (action === CEI_APPS_MENU_SOBRE) {
      this.menuAberto.set(false);
      this.sobreVersoesService.abrirDialogo();
      return;
    }

    if (action === CEI_APPS_MENU_SAIR) {
      this.logout();
    }
  }

  protected logout(): void {
    this.menuAberto.set(false);
    this.autenticacaoService.logout({ motivo: 'usuario' }).subscribe();
  }

  @HostListener('document:click')
  protected closePersonalMenuOnOutsideClick(): void {
    if (this.menuAberto()) {
      this.menuAberto.set(false);
    }
  }
}
